import React, { useState, useEffect } from 'react';
import VisualizationCanvas from './VisualizationCanvas';
import VisualizationErrorBoundary from './visualization/ErrorBoundary';

/**
 * VisualizationController - Manages the display of visualizations in response to
 * voice agent requests. This component sits between the LiveKitVoiceInput and the
 * VisualizationCanvas to handle visualization data flow and state.
 */
const VisualizationController = ({ children }) => {
  const [isVisualizationVisible, setIsVisualizationVisible] = useState(false);
  const [visualizationData, setVisualizationData] = useState(null);
  const [artifactId, setArtifactId] = useState(null); // New state for artifact ID

  // Add body class when visualization is visible for split-screen layout
  useEffect(() => {
    if (isVisualizationVisible) {
      document.body.classList.add('visualization-active');
      
      // Dispatch custom event for other components to listen for
      window.dispatchEvent(new CustomEvent('visualization-visibility-change', {
        detail: { isVisible: true }
      }));
    } else {
      document.body.classList.remove('visualization-active');
      
      // Dispatch custom event for other components to listen for
      window.dispatchEvent(new CustomEvent('visualization-visibility-change', {
        detail: { isVisible: false }
      }));
    }
    
    return () => {
      // Cleanup on unmount
      document.body.classList.remove('visualization-active');
      
      // Final event dispatch on unmount
      window.dispatchEvent(new CustomEvent('visualization-visibility-change', {
        detail: { isVisible: false }
      }));
    };
  }, [isVisualizationVisible]);
  
  // Handler for visualization requests from LiveKitVoiceInput
  const handleVisualizationRequest = (data) => {
    // Handle case where data is undefined or null
    if (!data) {
      console.error('Received empty visualization data');
      return;
    }

    // Handle panel close command from voice
    if (data.type === 'close_panel') {
      setIsVisualizationVisible(false);
      setVisualizationData(null);
      setArtifactId(null);
      return;
    }

    // Handle sound effects from reading companion
    if (data.type === 'play_sound_effect') {
      try {
        // Convert base64 audio data to blob
        const audioData = atob(data.audio_data);
        const audioArray = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          audioArray[i] = audioData.charCodeAt(i);
        }
        const audioBlob = new Blob([audioArray], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Create and configure audio element
        const audio = new Audio(audioUrl);
        audio.volume = data.volume || 0.2; // Subtle volume by default

        // Play the sound effect
        audio.play().catch(error => {
          console.error('Error playing sound effect:', error);
        });

        // Clean up the URL after playing
        audio.addEventListener('ended', () => {
          URL.revokeObjectURL(audioUrl);
        });

      } catch (error) {
        console.error('Error processing sound effect:', error);
      }
      return;
    }

    // Fast path: template_visual and artifact_visual from create_visual tool
    // Pass through directly to VisualizationCanvas — no normalization needed
    if (data.type === 'template_visual' || data.type === 'artifact_visual') {
      if (data.type === 'artifact_visual' && data.artifactId) {
        setArtifactId(data.artifactId);
      } else {
        setArtifactId(null);
      }
      setVisualizationData(data);
      setIsVisualizationVisible(true);
      return;
    }

    // Check for artifact ID - this is now the primary path
    if (data.artifactId) {
      setArtifactId(data.artifactId);
      
      // Set basic visualization metadata if available
      const normalizedData = {
        topic: (data.topic || data.visualization?.topic || 'Interactive Visualization'),
        context: (data.context || data.visualization?.context || ''),
        timestamp: (data.timestamp || data.visualization?.timestamp || Date.now().toString()),
        meta: {
          ...(data.meta || {}),
          format: 'artifact',
          rendering_mode: 'iframe',
          is_artifact: true
        }
      };
      
      // Update state
      setVisualizationData(normalizedData);
      setIsVisualizationVisible(true);
      return;
    }
    
    // Legacy path for compatibility - Extract componentCode from potentially nested data structures
    let extractedComponentCode = null;

    // Direct componentCode field (highest priority)
    if (data.componentCode) {
      extractedComponentCode = data.componentCode;
    }
    // Check for componentCode in visualization field (common in RPC pattern)
    else if (data.visualization && data.visualization.componentCode) {
      extractedComponentCode = data.visualization.componentCode;
    }
    // Check for component in direct API response (visualization_api.py format)
    else if (data.component && typeof data.component === 'string') {
      extractedComponentCode = data.component;
    }

    // Normalize the data structure to handle different formats
    const normalizedData = {
      topic: (data.topic || data.visualization?.topic || 'Educational Visualization'),
      context: (data.context || data.visualization?.context || ''),
      timestamp: (data.timestamp || data.visualization?.timestamp || Date.now().toString()),
      componentCode: extractedComponentCode || null,
      ...data // Preserve any additional fields
    };

    // Check if we have a componentCode in the data and ensure it has the right format
    if (normalizedData.componentCode) {
      // Ensure component code looks like React (has import React)
      if (typeof normalizedData.componentCode === 'string' &&
          !normalizedData.componentCode.includes('import React')) {
        normalizedData.componentCode = `import React, { useState, useEffect } from 'react';\n\n${normalizedData.componentCode}`;
      }

      // Ensure there's an export default if not present
      if (typeof normalizedData.componentCode === 'string' &&
          !normalizedData.componentCode.includes('export default')) {
        // Look for component name pattern
        const componentMatch = normalizedData.componentCode.match(/function\s+([A-Za-z0-9_]+)/);
        const constComponentMatch = normalizedData.componentCode.match(/const\s+([A-Za-z0-9_]+)\s*=/);

        const componentName = componentMatch ? componentMatch[1] :
                            constComponentMatch ? constComponentMatch[1] : 'Visualization';

        normalizedData.componentCode += `\n\nexport default ${componentName};`;
      }
    }

    // Set metadata for better component rendering
    normalizedData.meta = {
      format: 'react',
      rendering_mode: 'component',
      is_direct_component: true,
      ...normalizedData.meta
    };

    // Update state to trigger visualization display
    setVisualizationData(normalizedData);
    setArtifactId(null); // Clear any existing artifact ID
    setIsVisualizationVisible(true);
    
    // Track the visualization in analytics (if available)
    try {
      if (typeof window.posthog !== 'undefined') {
        window.posthog.capture('visualization_created', {
          topic: normalizedData.topic,
          timestamp: normalizedData.timestamp,
          source: normalizedData.source || 'unknown'
        });
      }
    } catch (e) {
      console.warn('Error tracking visualization:', e);
    }
  };
  
  // Handler for closing the visualization
  const handleCloseVisualization = () => {
    setIsVisualizationVisible(false);
    
    // Optional: Clear visualization data after a delay to allow for exit animation
    setTimeout(() => {
      if (!isVisualizationVisible) {
        setVisualizationData(null);
      }
    }, 500);
  };
  
  // Handler for visualization errors
  const handleVisualizationError = (error) => {
    console.error('Visualization error:', error);
    // Optionally show an error notification or handle in another way
  };
  
  // Clone the children to pass the visualization request handler
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { onVisualizationRequest: handleVisualizationRequest });
    }
    return child;
  });
  
  return (
    <>
      {/* Pass the visualization handler to children */}
      {childrenWithProps}
      
      {/* Visualization Canvas */}
      <VisualizationErrorBoundary>
        <VisualizationCanvas
          isVisible={isVisualizationVisible}
          visualizationData={visualizationData}
          artifactId={artifactId}
          onClose={handleCloseVisualization}
          onError={handleVisualizationError}
        />
      </VisualizationErrorBoundary>
    </>
  );
};

export default VisualizationController;